import { Location } from "@angular/common";
import { Injectable } from "@angular/core";
import { CacheFactory, CacheStore, fromMinutesToMilliseconds, StorageMode, WindowRef } from "@obg/ngx.global";
import { Subscription } from "rxjs";
import { filter, take } from "rxjs/operators";

import { RouteService } from "./route.service";

const LAST_EXTERNAL_URL = "lastExternalUrl";

@Injectable()
export class HistoryService {
	constructor(private location: Location) {}

	back() {
		this.location.back();
	}

	appInitialized() {
		// noop
	}
}

/**
 * This service is a workaround to handle when the same url is added to the history multiple times.
 * We have some iframe providers (payment, KYC) that are pushing multiple items to the history when customer navigates within the iframe.
 * Even if some of our providers fixes the problem, we cannot be sure that an integration later on will not corrupt our history again.
 */
@Injectable()
export class AgressiveHistoryService {
	private cache: CacheStore;

	constructor(
		private windowRef: WindowRef,
		private routeService: RouteService,
		cacheFactory: CacheFactory
	) {
		this.cache = cacheFactory.get("router.history", {
			isLanguageSpecific: true,
			storageMode: StorageMode.LocalStorage,
			maxAge: fromMinutesToMilliseconds(2),
		});
	}

	get href(): string {
		return this.windowRef.native.location.href;
	}

	/**
	 * Like Angular's `Location.back()` but ensures that the url changes when it is called.
	 */
	back() {
		const externalUrl = this.href;
		this.storeCurrentBackUrl(externalUrl).then(() => this.backUntilUrlChanges(externalUrl));
	}

	/**
	 * It might happen that an iframe pushes an entry to the history that will do a full page reload
	 * when calling `history.back` even though the url will stay the same (e.g.: Payment 3-D secure).
	 * This method handles that scenario (e.g.: customer should not stay on deposit page after a successful deposit).
	 */
	appInitialized(): Subscription {
		return this.cache
			.getItemAsObservable(LAST_EXTERNAL_URL)
			.pipe(filter((url) => !!url))
			.subscribe((url) => (url === this.href ? this.back() : this.storeCurrentBackUrl(undefined)));
	}

	private backUntilUrlChanges(url: string | undefined): Subscription {
		const subscription = this.routeService
			.onPopState$()
			.pipe(take(1))
			.subscribe(() => (this.href !== url ? this.storeCurrentBackUrl(undefined) : this.backUntilUrlChanges(url)));
		this.windowRef.native.history.back();
		return subscription;
	}

	private storeCurrentBackUrl(url: string | undefined): Promise<string | undefined> {
		return this.cache.setItem(LAST_EXTERNAL_URL, url);
	}
}
